import { useState, useEffect } from "react";
import { InventoryManagementDialog } from "@/components/inventory/InventoryManagementDialog";
import { EditInventoryDialog } from "@/components/inventory/EditInventoryDialog";
import { DeleteInventoryConfirmDialog } from "@/components/inventory/DeleteInventoryConfirmDialog";
import { InventorySummary } from "@/components/inventory/InventorySummary";
import { InventoryAlerts } from "@/components/inventory/InventoryAlerts";
import { InventoryChart } from "@/components/inventory/InventoryChart";
import { MenuInventoryRelationships } from "@/components/inventory/MenuInventoryRelationships";
import { ManuallyTrackedItems } from "@/components/inventory/ManuallyTrackedItems";
import { Button } from "@/components/ui/button";
import {
  Plus,
  RefreshCw,
  Database,
  AlertCircle,
  Edit,
  Trash2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { ProgressWithIndicator } from "@/components/ui/progress-with-indicator";
import { Badge } from "@/components/ui/badge";
import {
  getAdvancedInventoryItems,
  syncAllMenuItemsAvailability,
} from "@/services/inventory/inventoryService";
import { format } from "date-fns";
import { analyticsEvents } from "@/services/analytics";
import { useFeatureFlagStore } from "@/store/featureFlagStore";

interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  current_quantity: number;
  minimum_quantity: number;
  unit_of_measure: string;
  store_id: string;
  created_at: string;
  updated_at: string;
  inventory_type: string;
  expiration_date?: string;
  expiry_date?: string; // Added this field to handle legacy data
  reorder_point?: number;
}

const STORAGE_KEY = "selectedInventoryStoreId";

const InventoryManagement = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const {
    selectedStoreId,
    setSelectedStoreId,
    stores,
    toggleInventoryTracking,
  } = useStoreManagementStore();
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();

  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("inventory");
  const { advancedInventoryTracking } = useFeatureFlagStore();

  // For editing inventory items
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // For deleting inventory items
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Get the selected store's advanced inventory tracking setting
  const selectedStore = stores.find((store) => store.id === selectedStoreId);
  const isAdvancedMode = selectedStore?.advanced_inventory_tracking || false;

  useEffect(() => {
    if (selectedStoreId) {
      fetchInventoryItems();
      const subscription = setupRealtimeSubscription();

      // Track page view with store information
      analyticsEvents.trackPageView("inventory_management", {
        store_id: selectedStoreId,
        advanced_mode: isAdvancedMode,
      });

      // Sync all menu items availability when the store is selected or changes
      syncAllMenuItemsAvailability(selectedStoreId).then((result) => {
        if (result.success) {
          console.log(
            `Synced availability for ${result.updatedItems} of ${result.totalItems} menu items`
          );
        }
      });

      return () => {
        if (subscription) {
          supabase.removeChannel(subscription);
        }
      };
    }
  }, [selectedStoreId]);

  const setupRealtimeSubscription = () => {
    if (!selectedStoreId) return null;

    console.log(
      `Setting up realtime subscription for store: ${selectedStoreId}`
    );

    const channel = supabase
      .channel(`inventory-changes-${selectedStoreId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "inventory_items",
          filter: `store_id=eq.${selectedStoreId}`,
        },
        (payload) => {
          console.log("Realtime update received:", payload);
          // Refresh inventory items when changes occur
          if (
            payload.new &&
            "inventory_type" in payload.new &&
            payload.new.inventory_type === "advanced"
          ) {
            fetchInventoryItems();
          }
          // We'll handle manual inventory refresh through the onRefresh prop
        }
      )
      .subscribe((status) => {
        console.log(`${t("inventory.subscription.error.closed")}`);
      });

    return channel;
  };

  const fetchInventoryItems = async () => {
    if (!selectedStoreId) return;

    setLoading(true);
    try {
      console.log(
        `Fetching advanced inventory items for store: ${selectedStoreId}`
      );

      // Use getAdvancedInventoryItems to only get items with inventory_type='advanced'
      const items = await getAdvancedInventoryItems(selectedStoreId);

      if (items && items.length > 0) {
        console.log("Fetched advanced inventory items:", items);
        setInventoryItems(items);
      } else {
        // If no items are returned, set empty array
        setInventoryItems([]);
      }
    } catch (error) {
      console.error("Error fetching inventory items:", error);
      toast({
        title: t("error"),
        description: t("failedToLoadInventory"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsEditDialogOpen(true);

    analyticsEvents.trackEvent("inventory_edit_clicked", {
      item_id: item.id,
      item_name: item.name,
      store_id: selectedStoreId,
    });
  };

  const handleDeleteItem = (id: string, name: string) => {
    setItemToDelete({ id, name });
    setIsDeleteDialogOpen(true);

    analyticsEvents.trackEvent("inventory_delete_clicked", {
      item_id: id,
      item_name: name,
      store_id: selectedStoreId,
    });
  };

  const handleToggleAdvancedMode = async (checked: boolean) => {
    if (selectedStoreId) {
      // Only proceed if feature flag allows it
      if (!advancedInventoryTracking && checked) {
        toast({
          title: "Feature Not Available",
          description: "Advanced inventory tracking is coming soon!",
        });
        return;
      }

      const success = await toggleInventoryTracking(selectedStoreId, checked);
      if (success) {
        analyticsEvents.inventory.modeToggled(selectedStoreId, checked);

        toast({
          title: checked ? "Advanced Mode Enabled" : "Advanced Mode Disabled",
          description: checked
            ? "Menu items can now be linked to inventory items for real-time tracking"
            : "Reverted to basic inventory tracking mode",
        });
      }
    }
  };

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
    analyticsEvents.trackEvent("inventory_store_changed", {
      store_id: storeId,
    });
  };

  const getStockStatus = (current: number, minimum: number) => {
    const ratio = current / Math.max(minimum, 1);
    if (ratio <= 0)
      return {
        color: "destructive",
        label: t("inventory.status.outOfStock"),
        indicator: "bg-destructive",
      };
    if (ratio < 1)
      return {
        color: "amber",
        label: t("inventory.status.lowStock"),
        indicator: "bg-amber-500",
      };
    return {
      color: "green",
      label: t("inventory.status.inStock"),
      indicator: "bg-green-500",
    };
  };

  const getProgressValue = (current: number, minimum: number) => {
    // Calculate percentage of current quantity relative to 2x the minimum for better visualization
    const target = minimum * 2;
    const percentage = Math.min(
      100,
      Math.round((current / Math.max(target, 1)) * 100)
    );
    return percentage;
  };

  const refreshInventory = () => {
    fetchInventoryItems();

    // Also sync menu item availability when refreshing inventory
    if (selectedStoreId) {
      syncAllMenuItemsAvailability(selectedStoreId).then((result) => {
        if (result.success) {
          console.log(
            `Synced availability for ${result.updatedItems} of ${result.totalItems} menu items`
          );
        }
      });
    }

    analyticsEvents.trackEvent("inventory_refreshed", {
      store_id: selectedStoreId,
    });

    toast({
      title: "Inventory Refreshed",
      description:
        "Latest inventory data has been loaded and menu items synced",
    });
  };

  // Set active tab based on advanced mode
  useEffect(() => {
    if (isAdvancedMode) {
      setActiveTab("inventory");
    } else {
      setActiveTab("manual-tracking");
    }
  }, [isAdvancedMode]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Track inventory item creation
  const handleInventoryItemAdded = () => {
    fetchInventoryItems();
    // Analytics event is tracked inside the dialog component
  };

  // Track inventory item update
  const handleInventoryItemUpdated = () => {
    fetchInventoryItems();
    // Analytics event is tracked inside the dialog component
  };

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl md:text-3xl font-bold tracking-tight">
              {t("inventory.management.title")}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={refreshInventory}
              title="Refresh inventory"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {advancedInventoryTracking && (
            <div className="flex items-center space-x-2">
              <Switch
                id="inventory-mode"
                checked={isAdvancedMode}
                onCheckedChange={handleToggleAdvancedMode}
              />
              <Label htmlFor="inventory-mode" className="cursor-pointer">
                {t("inventory.management.advancedMode")}
              </Label>
            </div>
          )}

          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("inventory.management.addItem")}
          </Button>
        </div>
      </div>

      <Tabs
        defaultValue="inventory"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList
          className={`grid w-full ${
            isAdvancedMode ? "grid-cols-2" : "grid-cols-2"
          }`}
        >
          {isAdvancedMode && (
            <TabsTrigger value="inventory">
              {t("inventory.tabs.advancedTracking")}
            </TabsTrigger>
          )}
          <TabsTrigger value="menu-links">
            {t("inventory.tabs.menuRelationships")}
          </TabsTrigger>
          {!isAdvancedMode && (
            <TabsTrigger value="manual-tracking">
              {t("inventory.tabs.manuallyTracked")}
            </TabsTrigger>
          )}
        </TabsList>

        {isAdvancedMode && (
          <TabsContent value="inventory">
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
              <InventorySummary
                storeId={selectedStoreId}
                isLoading={loading}
                inventory={inventoryItems}
              />
              <InventoryAlerts
                storeId={selectedStoreId}
                isLoading={loading}
                inventory={inventoryItems}
              />
              <InventoryChart
                storeId={selectedStoreId}
                isLoading={loading}
                inventory={inventoryItems}
              />
            </div>

            <div className="mt-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Database className="mr-2 h-5 w-5" />
                    {t("inventory.management.advancedInventoryItems")}
                  </h3>

                  {loading ? (
                    <div className="h-40 flex items-center justify-center">
                      <p className="text-muted-foreground">
                        {t("inventory.management.loading")}
                      </p>
                    </div>
                  ) : inventoryItems.length === 0 ? (
                    <div className="h-40 flex flex-col items-center justify-center gap-2 text-center">
                      <AlertCircle className="h-10 w-10 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">
                        {t("inventory.management.noItems")}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsAddDialogOpen(true)}
                        className="mt-2"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("inventory.management.addFirstItem")}
                      </Button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t("inventory.table.name")}</TableHead>
                            <TableHead>
                              {t("inventory.table.category")}
                            </TableHead>
                            <TableHead>
                              {t("inventory.table.stockLevel")}
                            </TableHead>
                            <TableHead>{t("inventory.table.status")}</TableHead>
                            <TableHead>
                              {t("inventory.table.expirationDate")}
                            </TableHead>
                            <TableHead>
                              {t("inventory.table.reorderPoint")}
                            </TableHead>
                            <TableHead className="text-right">
                              {t("inventory.table.actions")}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inventoryItems.map((item) => {
                            const stockStatus = getStockStatus(
                              item.current_quantity,
                              item.minimum_quantity
                            );
                            const progressValue = getProgressValue(
                              item.current_quantity,
                              item.minimum_quantity
                            );
                            // Use either expiration_date or expiry_date, whichever is available
                            const expirationDate =
                              item.expiration_date || item.expiry_date;

                            return (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                  {item.name}
                                </TableCell>
                                <TableCell>
                                  {item.description || "N/A"}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col gap-2 w-full max-w-[200px]">
                                    <ProgressWithIndicator
                                      value={progressValue}
                                      className="h-2"
                                      indicatorClassName={stockStatus.indicator}
                                    />
                                    <span className="text-xs">
                                      {item.current_quantity} /{" "}
                                      {item.minimum_quantity}{" "}
                                      {item.unit_of_measure}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant={
                                      stockStatus.color === "destructive"
                                        ? "destructive"
                                        : "outline"
                                    }
                                    className={`${
                                      stockStatus.color === "amber"
                                        ? "border-amber-500 text-amber-500"
                                        : ""
                                    } 
                                               ${
                                                 stockStatus.color === "green"
                                                   ? "border-green-500 text-green-500"
                                                   : ""
                                               }`}
                                  >
                                    {stockStatus.label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {formatDate(expirationDate)}
                                </TableCell>
                                <TableCell>
                                  {item.reorder_point !== undefined
                                    ? item.reorder_point
                                    : "N/A"}
                                </TableCell>
                                <TableCell className="text-right space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditItem(item)}
                                  >
                                    <Edit className="h-4 w-4 mr-1" /> Edit
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() =>
                                      handleDeleteItem(item.id, item.name)
                                    }
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </TabsContent>
        )}

        <TabsContent value="menu-links">
          <MenuInventoryRelationships
            storeId={selectedStoreId}
            isAdvancedMode={isAdvancedMode}
          />
        </TabsContent>

        {!isAdvancedMode && (
          <TabsContent value="manual-tracking">
            <ManuallyTrackedItems
              translate={t}
              storeId={selectedStoreId}
              isAdvancedMode={isAdvancedMode}
              isLoading={loading}
              onRefresh={refreshInventory}
            />
          </TabsContent>
        )}
      </Tabs>

      {isAddDialogOpen && (
        <InventoryManagementDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          storeId={selectedStoreId}
          onInventoryAdded={handleInventoryItemAdded}
          isAdvancedMode={isAdvancedMode}
          refreshInventory={refreshInventory}
        />
      )}

      {isEditDialogOpen && selectedItem && (
        <EditInventoryDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          inventoryItem={selectedItem}
          onInventoryUpdated={handleInventoryItemUpdated}
        />
      )}

      {isDeleteDialogOpen && itemToDelete && (
        <DeleteInventoryConfirmDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          itemId={itemToDelete.id}
          itemName={itemToDelete.name}
          onDeleted={fetchInventoryItems}
        />
      )}
    </div>
  );
};

export default InventoryManagement;
